// src/services/creditRiskService.js
import { toast } from 'react-toastify';
import AuthService from './authService';

class CreditRiskService {
  static get apiBase() {
    return window.APP_CONFIG?.apiUrl || 'https://api.dpg.net.tr';
    //return process.env.REACT_APP_API_URL || 'https://localhost:44330';
  }

  // Get available limit for user
  static getAvailableLimit = async (userId) => {
    try {
      const response = await fetch(`${this.apiBase}/api/CreditRisk/getAvailableLimit?userId=${userId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${AuthService.getAuthToken()}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const text = await response.text();
        if (!text) {
          return {
            success: true,
            data: 0
          };
        }
        const result = JSON.parse(text);
        return {
          success: true,
          data: result.data
        };
      } else {
        const text = await response.text();
        let errorMessage = 'Available limit could not be retrieved';
        
        if (text) {
          try {
            const error = JSON.parse(text);
            errorMessage = error.message || errorMessage;
          } catch (e) {
            errorMessage = response.statusText || errorMessage;
          }
        }
        
        return {
          success: false,
          message: errorMessage,
          data: null
        };
      }
    } catch (error) {
      console.error('Error fetching available limit:', error);
      return {
        success: false,
        message: error.message || 'Server error occurred',
        data: null
      };
    }
  };

  // Get risks by user ID
  static getRisksByUserId = async (userId) => {
    try {
      const response = await fetch(`${this.apiBase}/api/CreditRisk/getByUserId/${userId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${AuthService.getAuthToken()}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const text = await response.text();
        if (!text) {
          // Empty response - return empty array
          return {
            success: true,
            data: []
          };
        }
        const result = JSON.parse(text);
        return {
          success: true,
          data: result.data || []
        };
      } else {
        // Handle error response
        const text = await response.text();
        let errorMessage = 'Credits could not be retrieved';
        
        if (response.status === 404) {
          // 404 means no risks found for this user - this is OK, return empty array
          return {
            success: true,
            data: []
          };
        }
        
        if (text) {
          try {
            const error = JSON.parse(text);
            errorMessage = error.message || errorMessage;
          } catch (e) {
            // If error response is not JSON, use status text
            errorMessage = response.statusText || errorMessage;
          }
        }
        
        return {
          success: false,
          message: errorMessage,
          data: []
        };
      }
    } catch (error) {
      // Don't log connection errors to avoid console spam when backend is unavailable
      // Only log if it's not a network/connection error
      if (error.name !== 'TypeError' || !error.message.includes('Failed to fetch')) {
        console.error('Error fetching risks by user ID:', error);
      }
      return {
        success: false,
        message: error.message || 'Server error occurred',
        data: []
      };
    }
  };

  // Get risks by credit ID
  static getRisksByCreditId = async (creditId) => {
    try {
      const response = await fetch(`${this.apiBase}/api/CreditRisk/getByCreditId/${creditId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${AuthService.getAuthToken()}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const text = await response.text();
        if (!text) {
          return {
            success: true,
            data: []
          };
        }
        const result = JSON.parse(text);
        return {
          success: true,
          data: result.data || []
        };
      } else {
        const text = await response.text();
        let errorMessage = 'Credits could not be retrieved';
        
        if (response.status === 404) {
          return {
            success: true,
            data: []
          };
        }
        
        if (text) {
          try {
            const error = JSON.parse(text);
            errorMessage = error.message || errorMessage;
          } catch (e) {
            errorMessage = response.statusText || errorMessage;
          }
        }
        
        return {
          success: false,
          message: errorMessage,
          data: []
        };
      }
    } catch (error) {
      // Don't log connection errors to avoid console spam when backend is unavailable
      // Only log if it's not a network/connection error
      if (error.name !== 'TypeError' || !error.message.includes('Failed to fetch')) {
        console.error('Error fetching risks by credit ID:', error);
      }
      return {
        success: false,
        message: error.message || 'Server error occurred',
        data: []
      };
    }
  };

  // Get active risks by user ID
  static getActiveRisks = async (userId) => {
    try {
      const response = await fetch(`${this.apiBase}/api/CreditRisk/getActiveRisks/${userId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${AuthService.getAuthToken()}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const text = await response.text();
        if (!text) {
          return {
            success: true,
            data: []
          };
        }
        const result = JSON.parse(text);
        return {
          success: true,
          data: result.data || []
        };
      } else {
        const text = await response.text();
        let errorMessage = 'Active credits could not be retrieved';
        
        if (response.status === 404) {
          return {
            success: true,
            data: []
          };
        }
        
        if (text) {
          try {
            const error = JSON.parse(text);
            errorMessage = error.message || errorMessage;
          } catch (e) {
            errorMessage = response.statusText || errorMessage;
          }
        }
        
        return {
          success: false,
          message: errorMessage,
          data: []
        };
      }
    } catch (error) {
      console.error('Error fetching active credits:', error);
      return {
        success: false,
        message: error.message || 'Server error occurred',
        data: []
      };
    }
  };

  // Close a risk (Admin only)
  static closeRisk = async (riskId, paymentDate = null) => {
    try {
      const response = await fetch(`${this.apiBase}/api/CreditRisk/close/${riskId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${AuthService.getAuthToken()}`,
        },
        body: JSON.stringify({ 
          riskId,
          paymentDate: paymentDate || new Date().toISOString()
        })
      });

      if (response.ok) {
        const text = await response.text();
        let message = 'Credit closed';
        if (text) {
          try {
            const result = JSON.parse(text);
            message = result.message || message;
          } catch (e) {
            // Ignore parse error, use default message
          }
        }
        toast.success(message);
        return {
          success: true,
          message: message
        };
      } else {
        const text = await response.text();
        let errorMessage = 'Credit could not be closed';
        if (text) {
          try {
            const error = JSON.parse(text);
            errorMessage = error.message || errorMessage;
          } catch (e) {
            errorMessage = response.statusText || errorMessage;
          }
        }
        toast.error(errorMessage);
        return {
          success: false,
          message: errorMessage
        };
      }
    } catch (error) {
      console.error('Error closing credit:', error);
      toast.error('Server error occurred');
      return {
        success: false,
        message: 'Server error occurred'
      };
    }
  };

  // Convert risk from GayriNakdi to Nakdi (Admin only)
  static convertRisk = async (riskId, interestRate = null) => {
    try {
      const response = await fetch(`${this.apiBase}/api/CreditRisk/convert/${riskId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${AuthService.getAuthToken()}`,
        },
        body: JSON.stringify({ 
          riskId,
          interestRate
        })
      });

      if (response.ok) {
        const text = await response.text();
        let message = 'Credit converted to overdraft';
        if (text) {
          try {
            const result = JSON.parse(text);
            message = result.message || message;
          } catch (e) {
            // Ignore parse error, use default message
          }
        }
        toast.success(message);
        return {
          success: true,
          message: message
        };
      } else {
        const text = await response.text();
        let errorMessage = 'Credit could not be converted to overdraft';
        if (text) {
          try {
            const error = JSON.parse(text);
            errorMessage = error.message || errorMessage;
          } catch (e) {
            errorMessage = response.statusText || errorMessage;
          }
        }
        toast.error(errorMessage);
        return {
          success: false,
          message: errorMessage
        };
      }
    } catch (error) {
      console.error('Error converting credit:', error);
      toast.error('Server error occurred');
      return {
        success: false,
        message: 'Server error occurred'
      };
    }
  };
}

export default CreditRiskService;

